import React, { useMemo } from 'react';
import { Modal, Badge, Spinner, Accordion } from 'react-bootstrap';
import { useQuery } from '@apollo/client/react';
import moment from 'moment';

import { GET_EVENT_FEEDBACK } from 'helper/graphql/queries';

// Resolve question metadata from feedbackFormSchema
const getQuestion = (schema, questionName) => {
	if (!schema?.pages) return null;
	for (const page of schema.pages) {
		for (const el of page.elements || []) {
			if (el.name === questionName) return el;
		}
	}
	return null;
};

const formatAnswer = (value, question) => {
	if (Array.isArray(value)) return value.join(', ');
	if (question?.type === 'rating') {
		const max = question.rateMax || question.rateCount || 5;
		const filled = Number(value);
		return (
			<span className="efm-rating">
				{'★'.repeat(filled)}{'☆'.repeat(Math.max(0, max - filled))}
				<span className="efm-rating-num"> {filled}/{max}</span>
			</span>
		);
	}
	if (question?.type === 'boolean') return value ? 'Yes' : 'No';
	return String(value);
};

const FeedbackEntry = ({ entry, schema, index }) => {
	const raw = entry.feedbackData;
	const answers = useMemo(() => {
		if (!raw) return {};
		try { return typeof raw === 'string' ? JSON.parse(raw) : raw; } catch { return {}; }
	}, [raw]);
	const keys = Object.keys(answers);

	return (
		<Accordion.Item eventKey={String(index)} className="efm-accordion-item">
			<Accordion.Header className="efm-accordion-header">
				<div className="efm-entry-header">
					<div className="efm-entry-name">
						{entry.user?.firstName} {entry.user?.lastName}
					</div>
					<div className="efm-entry-meta">
						<span className="efm-entry-email">{entry.user?.email}</span>
						<span className="efm-entry-date">
							{moment(entry.submittedAt).format('MMM D, YYYY · h:mm A')}
						</span>
					</div>
				</div>
			</Accordion.Header>
			<Accordion.Body className="efm-accordion-body">
				{keys.length === 0 ? (
					<p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>No answers recorded.</p>
				) : (
					<div className="efm-answers">
						{keys.map((key) => {
							const q = getQuestion(schema, key);
							return (
								<div key={key} className="efm-answer-row">
									<p className="efm-question">{q?.title || q?.name || key}</p>
									<p className="efm-answer">{formatAnswer(answers[key], q)}</p>
								</div>
							);
						})}
					</div>
				)}
			</Accordion.Body>
		</Accordion.Item>
	);
};

const EventFeedbackModal = ({ show, onHide, event }) => {
	const { data, loading } = useQuery(GET_EVENT_FEEDBACK, {
		variables: { guid: event?.guid },
		skip: !show || !event?.guid,
		fetchPolicy: 'network-only',
	});

	const eventNode = useMemo(
		() => data?.allEvents?.edges?.[0]?.node || null,
		[data]
	);

	const feedbackList = useMemo(
		() => eventNode?.feedback?.edges?.map((e) => e.node) || [],
		[eventNode]
	);

	const schema = useMemo(() => {
		const raw = eventNode?.feedbackFormSchema;
		if (!raw) return null;
		try { return typeof raw === 'string' ? JSON.parse(raw) : raw; } catch { return null; }
	}, [eventNode]);

	return (
		<Modal show={show} onHide={onHide} size="lg" centered>
			<Modal.Header closeButton className="efm-header">
				<div>
					<Modal.Title className="efm-title">Feedback Responses</Modal.Title>
					{event && <p className="efm-event-name mb-0">{event.title}</p>}
				</div>
			</Modal.Header>

			<Modal.Body className="p-0">
				{loading && (
					<div className="efm-loading">
						<Spinner animation="border" size="sm" className="me-2" />
						Loading feedback…
					</div>
				)}

				{!loading && feedbackList.length === 0 && (
					<div className="efm-empty">
						<i className="fe fe-message-square" style={{ fontSize: '2.5rem', opacity: 0.2 }}></i>
						<p className="mt-3 mb-0 text-muted">No feedback submitted yet.</p>
					</div>
				)}

				{!loading && feedbackList.length > 0 && (
					<>
						<div className="efm-count-bar">
							<Badge bg="" className="efm-count-badge">
								{feedbackList.length} response{feedbackList.length !== 1 ? 's' : ''}
							</Badge>
						</div>
						<div className="efm-list">
							<Accordion flush>
								{feedbackList.map((entry, i) => (
									<FeedbackEntry
										key={entry.guid}
										entry={entry}
										schema={schema}
										index={i}
									/>
								))}
							</Accordion>
						</div>
					</>
				)}
			</Modal.Body>

			<style>{`
				.efm-header {
					border-bottom: 2px solid #f1f5f9;
					padding: 1.25rem 1.5rem;
				}
				.efm-title { font-size: 1.0625rem; font-weight: 700; color: #1e293b; margin-bottom: 0.2rem; }
				.efm-event-name { font-size: 0.8125rem; color: #94a3b8; }
				.efm-loading, .efm-empty {
					display: flex; flex-direction: column; align-items: center;
					justify-content: center; padding: 3rem 2rem; color: #94a3b8; font-size: 0.875rem;
				}
				.efm-loading { flex-direction: row; }
				.efm-count-bar {
					padding: 0.75rem 1.5rem;
					background: #f8fafc;
					border-bottom: 1px solid #e2e8f0;
				}
				.efm-count-badge {
					background: rgba(246,133,33,0.1) !important;
					color: #F68521 !important;
					font-size: 0.75rem; font-weight: 700;
					padding: 4px 10px; border-radius: 8px;
				}
				.efm-list { max-height: 520px; overflow-y: auto; }
				.efm-accordion-item { border-bottom: 1px solid #f1f5f9 !important; border: none; }
				.efm-accordion-header .accordion-button {
					padding: 0.875rem 1.5rem;
					background: white;
					box-shadow: none;
					font-size: 0.875rem;
				}
				.efm-accordion-header .accordion-button:not(.collapsed) {
					background: #fafbfc;
					color: inherit;
					box-shadow: none;
				}
				.efm-entry-header { display: flex; flex-direction: column; gap: 0.2rem; }
				.efm-entry-name { font-weight: 700; font-size: 0.9rem; color: #1e293b; }
				.efm-entry-meta { display: flex; gap: 1rem; align-items: center; }
				.efm-entry-email { font-size: 0.8rem; color: #64748b; }
				.efm-entry-date { font-size: 0.75rem; color: #94a3b8; }
				.efm-accordion-body { padding: 0.75rem 1.5rem 1rem; background: #fafbfc; }
				.efm-answers { display: flex; flex-direction: column; gap: 0.875rem; }
				.efm-answer-row { border-left: 3px solid #e2e8f0; padding-left: 0.875rem; }
				.efm-question { font-size: 0.8125rem; font-weight: 600; color: #64748b; margin-bottom: 0.2rem; }
				.efm-answer { font-size: 0.9rem; color: #1e293b; margin: 0; }
				.efm-rating { color: #F68521; font-size: 1.1rem; letter-spacing: 1px; }
				.efm-rating-num { font-size: 0.8rem; color: #94a3b8; font-weight: 600; }
			`}</style>
		</Modal>
	);
};

export default EventFeedbackModal;
